import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { SeverityBadge, bandCopy } from "@/components/SeverityBadge";
import { useI18n } from "@/i18n";
import { ArrowRight, ChevronLeft } from "lucide-react";

export default function ReportView() {
  const { id } = useParams();
  const { t } = useI18n();
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get(`/reports/${id}`).then((r) => setReport(r.data));
  }, [id]);

  if (!report) return <div className="animate-pulse text-[color:var(--mano-ink-soft)]">Loading report…</div>;

  const copy = bandCopy(report.band, t);
  const isVoice = report.kind === "voice";
  const isHigh = report.band === "high";

  return (
    <div className="space-y-6" data-testid="report-view">
      <Link to="/home" className="inline-flex items-center gap-1 uppercase-label" data-testid="report-back">
        <ChevronLeft size={14} /> Home
      </Link>

      <div className={`rounded-3xl border border-[color:var(--mano-border)] p-8 bg-white`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <span className="uppercase-label">
              {isVoice ? "Voice Report" : "Questionnaire"} · {new Date(report.created_at).toLocaleString()}
            </span>
            <h1 className="font-display text-3xl mt-2">
              {isVoice ? `Voice — ${report.category.replace("_", " ")}` : report.questionnaire_name}
            </h1>
          </div>
          <SeverityBadge band={report.band} size="lg" />
        </div>

        <div className={`mt-6 rounded-2xl p-5 band-${report.band}`}>
          <div className="font-display text-xl">{copy.title}</div>
          <div className="text-sm mt-2 leading-relaxed">{copy.body}</div>
        </div>

        {isVoice && (
          <>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {Object.entries(report.sub_domains || {}).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[color:var(--mano-border)] p-4">
                  <div className="uppercase-label">{k}</div>
                  <div className="mt-1"><SeverityBadge band={v} /></div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-sm text-[color:var(--mano-ink-soft)]">
              Model: <span className="font-mono">{report.model_version}</span> · Confidence: {(report.confidence * 100).toFixed(0)}%
            </div>
          </>
        )}

        {!isVoice && (
          <div className="mt-6 rounded-xl border border-[color:var(--mano-border)] p-4">
            <div className="uppercase-label">Total score</div>
            <div className="font-display text-2xl mt-1">{report.total_score}</div>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-6">
        <div className="uppercase-label">Suggested next step</div>
        <div className="mt-3">
          {isHigh ? (
            <>
              <h3 className="font-display text-xl">Please connect with a clinical specialist</h3>
              <p className="text-[color:var(--mano-ink-soft)] mt-2">
                Given the severity, only clinical consultation is enabled from this report.
              </p>
              <Link
                to="/experts"
                data-testid="report-book-clinical"
                className="inline-flex items-center gap-1 mt-4 bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white px-5 py-2.5 rounded-full font-semibold transition-colors"
              >
                Book a clinical consult <ArrowRight size={14} />
              </Link>
              <div className="mt-3 text-xs text-[color:var(--mano-ink-soft)]">
                Crisis? Vandrevala Foundation Helpline: <b>1860 2662 345</b>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-3">
              <Link
                to="/experts"
                data-testid="report-book-clinical"
                className="inline-flex items-center gap-1 bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white px-5 py-2.5 rounded-full font-semibold transition-colors"
              >
                Speak to a specialist <ArrowRight size={14} />
              </Link>
              <Link
                to="/therapies"
                data-testid="report-browse-therapies"
                className="inline-flex items-center gap-1 border border-[color:var(--mano-border)] px-5 py-2.5 rounded-full font-semibold hover:bg-[color:var(--mano-border)]/30 transition-colors"
              >
                Explore therapies
              </Link>
              <Link
                to="/voicelab"
                data-testid="report-open-voicelab"
                className="inline-flex items-center gap-1 border border-[color:var(--mano-border)] px-5 py-2.5 rounded-full font-semibold hover:bg-[color:var(--mano-border)]/30 transition-colors"
              >
                Continue in Voice Lab
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
